import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Slide,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { makeRequest } from "../../../assets/functions_helper";

// ── Transición del modal ─────────────────────────────────────────────────────
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ── Estilos comunes ──────────────────────────────────────────────────────────
const FONT = "'Inter', 'Como', sans-serif";
const ACCENT = "#F72E41";
const ACCENT_DK = "#c0001d";

/**
 * CreateRestaurantModal
 *
 * Props:
 *   open         {boolean}  — controla visibilidad
 *   onClose      {fn}       — cierra el modal
 *   onSuccess    {fn}       — callback tras crear con éxito (recibe el nuevo restaurante)
 *   onUpgradePlan {fn}      — callback para redirigir a vista de planes
 */
export default function CreateRestaurantModal({
  open,
  onClose,
  onSuccess,
  onUpgradePlan,
}) {
  const [name, setName]             = useState("");
  const [isLoading, setIsLoading]   = useState(false);
  const [snack, setSnack]           = useState({ open: false, msg: "", severity: "success" });
  const [limitReached, setLimitReached] = useState(false);

  // ── Reset al cerrar ─────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isLoading) return;
    setName("");
    setLimitReached(false);
    onClose();
  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setSnack({ open: true, msg: "El nombre del restaurante es obligatorio.", severity: "warning" });
      return;
    }

    setIsLoading(true);
    setLimitReached(false);

    try {
      const response = await makeRequest(
        "POST",
        "restaurant_admin/create_restaurant",
        { name: trimmed },
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        true
      );

      setSnack({ open: true, msg: "¡Restaurante creado exitosamente!", severity: "success" });
      setName("");
      onClose();
      if (typeof onSuccess === "function") onSuccess(response);

    } catch (err) {
      // Error 0013 → límite del plan alcanzado
      const errorCode =
        (typeof err === "object" && err?.error_code) ||
        (typeof err === "string" && err.includes("0013") ? "0013" : null);

      if (errorCode === "0013" || (typeof err === "string" && err.includes("0013"))) {
        setLimitReached(true);
      } else {
        const msg =
          typeof err === "string"
            ? err
            : err?.message || "Error al crear el restaurante. Inténtalo de nuevo.";
        setSnack({ open: true, msg, severity: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
  };

  // ── Vista: límite de plan alcanzado ─────────────────────────────────────────
  if (limitReached) {
    return (
      <>
        <Dialog
          open={open}
          onClose={handleClose}
          TransitionComponent={SlideUp}
          PaperProps={{
            sx: {
              borderRadius: "20px",
              maxWidth: 420,
              width: "100%",
              fontFamily: FONT,
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
            },
          }}
        >
          {/* Header degradado */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              px: 3,
              py: 3.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "16px",
                background: "rgba(99,102,241,0.15)",
                border: "2px solid rgba(99,102,241,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <WorkspacePremiumIcon sx={{ fontSize: 30, color: "#818cf8" }} />
            </Box>
            <Typography
              sx={{ color: "#fff", fontWeight: 800, fontSize: 18, fontFamily: FONT, textAlign: "center" }}
            >
              Límite de restaurantes alcanzado
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.6)", fontSize: 13, fontFamily: FONT, textAlign: "center", lineHeight: 1.6 }}
            >
              Has alcanzado el límite de restaurantes de tu plan actual. Actualiza tu plan para crear más.
            </Typography>
          </Box>

          <DialogContent sx={{ px: 3, py: 3 }}>
            {/* Chip de estado */}
            <Box
              sx={{
                background: "#f8fafc",
                border: "1.5px solid #e2e8f0",
                borderRadius: "12px",
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: ACCENT,
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 13, color: "#475569", fontFamily: FONT, lineHeight: 1.55 }}>
                Tu plan actual tiene un límite de restaurantes. Actualiza a <strong>Pro</strong> o <strong>Enterprise</strong> para escalar sin límites.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.5, flexDirection: "column" }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<WorkspacePremiumIcon />}
              onClick={() => {
                handleClose();
                if (typeof onUpgradePlan === "function") onUpgradePlan();
              }}
              sx={{
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: "#fff",
                borderRadius: "13px",
                fontWeight: 800,
                fontSize: 14,
                py: 1.5,
                fontFamily: FONT,
                textTransform: "none",
                boxShadow: "0 4px 14px rgba(99,102,241,0.4)",
                "&:hover": { filter: "brightness(1.1)", boxShadow: "0 6px 18px rgba(99,102,241,0.45)" },
              }}
            >
              Actualizar Plan
            </Button>
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                background: "#f8fafc",
                color: "#64748b",
                border: "2px solid #e2e8f0",
                borderRadius: "13px",
                fontWeight: 700,
                fontSize: 13,
                py: 1.2,
                fontFamily: FONT,
                textTransform: "none",
                "&:hover": { borderColor: "#cbd5e1", background: "#f1f5f9" },
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar global */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} sx={{ borderRadius: "12px", fontFamily: FONT }}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </>
    );
  }

  // ── Vista: formulario de creación ───────────────────────────────────────────
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={SlideUp}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            maxWidth: 440,
            width: "100%",
            fontFamily: FONT,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #070724 0%, #1e293b 100%)",
            px: 3,
            py: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: `rgba(247,46,65,0.15)`,
              border: `2px solid rgba(247,46,65,0.3)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AddCircleOutlineIcon sx={{ color: "#f87171", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 17, fontFamily: FONT, lineHeight: 1.2 }}>
              Crear Restaurante
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, fontFamily: FONT, mt: 0.3 }}>
              Añade un nuevo restaurante a tu cuenta
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "#64748b", fontFamily: FONT, mb: 2, lineHeight: 1.6 }}>
            Asigna un nombre único e identificable para tu restaurante. Podrás personalizarlo más adelante.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Nombre del restaurante"
            placeholder="Ej. La Trattoria Centro"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            inputProps={{ maxLength: 80 }}
            helperText={`${name.length}/80 caracteres`}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: FONT,
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: ACCENT,
                },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
              "& .MuiFormHelperText-root": { textAlign: "right", fontFamily: FONT, fontSize: 11 },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              flex: 1,
              background: "#f8fafc",
              color: "#475569",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: 14,
              py: 1.2,
              fontFamily: FONT,
              textTransform: "none",
              "&:hover": { borderColor: "#cbd5e1", background: "#f1f5f9" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !name.trim()}
            sx={{
              flex: 2,
              background: isLoading || !name.trim()
                ? "#e2e8f0"
                : `linear-gradient(135deg, ${ACCENT}, ${ACCENT_DK})`,
              color: isLoading || !name.trim() ? "#94a3b8" : "#fff",
              borderRadius: "12px",
              fontWeight: 800,
              fontSize: 14,
              py: 1.2,
              fontFamily: FONT,
              textTransform: "none",
              boxShadow: isLoading || !name.trim() ? "none" : "0 4px 14px rgba(247,46,65,0.35)",
              transition: "all 0.2s ease",
              "&:hover": {
                filter: isLoading || !name.trim() ? "none" : "brightness(1.08)",
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "#94a3b8" }} />
                <span>Creando…</span>
              </Box>
            ) : (
              "✓ Crear Restaurante"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ borderRadius: "12px", fontFamily: FONT }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
